import { useEffect, useState } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import { Bookmark, BookmarkCheck, Calendar, Clock, ExternalLink, Gavel, Gift, Heart, MapPin, Tag } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { VerifiedBadge } from '@/components/VerifiedBadge'
import AddToCalendar from '@/components/AddToCalendar'
import ShareButtons from '@/components/ShareButtons'
import CartModal from '@/components/checkout/CartModal'
import DonateBlock from '@/components/checkout/DonateBlock'
import { formatTier } from '@/lib/format'
import { externalPlatformName } from '@/lib/externalPlatform'
import { track } from '@/lib/track'
import type { DetailOrganization, Event, SharedProps } from '@/types'

export function formatDate(dateStr: string): string {
  if (!dateStr) return ''
  const date = new Date(dateStr + 'T00:00:00')
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export function formatPrice(price: string | null): string {
  if (!price) return 'Price TBD'
  const num = parseFloat(price)
  if (num === 0) return 'Free'
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
}

export function useCountdown(dateStr: string, startTime: string | null) {
  const [timeLeft, setTimeLeft] = useState<{ days: number; hours: number; minutes: number } | null>(null)

  useEffect(() => {
    const target = new Date(`${dateStr}T00:00:00`)

    if (startTime) {
      const time = new Date(startTime)
      if (Number.isNaN(time.getTime())) {
        setTimeLeft(null)
        return
      }
      target.setHours(time.getUTCHours(), time.getUTCMinutes(), 0, 0)
    }

    function update() {
      const diff = target.getTime() - Date.now()
      if (diff <= 0) {
        setTimeLeft(null)
        return
      }
      setTimeLeft({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
      })
    }

    update()
    const id = setInterval(update, 60000)
    return () => clearInterval(id)
  }, [dateStr, startTime])

  return timeLeft
}

export function buildLocalityLine(event: Event): string | null {
  const locality = [event.city, event.state].filter(Boolean).join(', ')
  if (locality && event.zip) return `${locality} ${event.zip}`
  return locality || event.zip || null
}

export function buildLocationLines(event: Event): string[] {
  return [event.venue_name, event.street_address, buildLocalityLine(event)]
    .filter((line): line is string => Boolean(line))
}

export function buildAddressQuery(event: Event): string {
  return buildLocationLines(event).join(', ')
}

export function hasMapAddress(event: Event): boolean {
  return Boolean(event.street_address || event.city || event.state || event.zip)
}

export function HostsSection({ hosts }: { hosts: Event['hosts'] }) {
  if (!hosts.length) return null
  return (
    <div>
      <h2 className="mb-4 text-xl font-semibold">Hosted by</h2>
      <div className="space-y-3">
        {hosts.map((host, i) => (
          <div key={i} className="border-l-2 border-primary/30 pl-4">
            <p className="font-serif font-semibold">{host.name}</p>
            {host.organization && (
              <p className="text-base text-muted-foreground">{host.organization}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

export function HonoreesSection({ honorees }: { honorees: Event['honorees'] }) {
  if (!honorees.length) return null
  return (
    <div>
      <h2 className="mb-4 text-xl font-semibold">Honorees</h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {honorees.map((honoree, i) => (
          <div key={i} className="rounded-xl bg-muted/50 p-4">
            <p className="font-semibold">{honoree.name}</p>
            {honoree.company && (
              <p className="text-base text-muted-foreground">{honoree.company}</p>
            )}
            {honoree.award && <p className="mt-2 text-base">{honoree.award}</p>}
          </div>
        ))}
      </div>
    </div>
  )
}

export function EntertainmentSection({ entertainment }: { entertainment: Event['entertainment'] }) {
  if (!entertainment.length) return null
  return (
    <div>
      <h2 className="mb-4 text-xl font-semibold">Entertainment</h2>
      <div className="space-y-2">
        {entertainment.map((ent, i) => (
          <div key={i} className="border-l-2 border-muted-foreground/20 pl-3">
            <span className="font-medium">{ent.name}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export function SponsorsSection({ event, isPreview = false }: { event: Event; isPreview?: boolean }) {
  if (!event.sponsors.length) return null
  return (
    <div>
      <h2 className="mb-4 text-xl font-semibold">Sponsors</h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
        {event.sponsors.map((s, i) => {
          const tierLabel = formatTier(s.tier)
          const linkEnabled = !!s.url && !isPreview
          const cardClasses = `flex h-28 w-full items-center justify-center rounded-2xl border border-black/10 bg-card p-6 shadow-xs transition ${
            linkEnabled ? 'hover:border-primary/30 hover:shadow-sm' : ''
          } ${s.featured ? 'ring-1 ring-primary/30' : ''}`

          const inner = s.logo_url ? (
            <img src={s.logo_url} alt={s.name} className="max-h-16 w-full object-contain" />
          ) : (
            <span className="line-clamp-2 text-center text-base font-medium text-foreground text-balance">
              {s.name}
            </span>
          )

          const card = linkEnabled ? (
            <a
              href={s.url ?? undefined}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={s.name}
              className={cardClasses}
              onClick={() => track(event.id, 'sponsor_click', { name: s.name })}
            >
              {inner}
            </a>
          ) : (
            <div className={cardClasses}>{inner}</div>
          )

          return (
            <div key={`${s.name}-${i}`} className="flex flex-col items-center gap-2">
              {card}
              {tierLabel && <p className="text-sm text-muted-foreground">{tierLabel}</p>}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export function CommitteeSection({ members }: { members: Event['committee_members'] }) {
  if (!members.length) return null
  return (
    <div>
      <h2 className="mb-4 text-xl font-semibold">Gala Committee</h2>
      <div className="space-y-1">
        {members.map((member, i) => (
          <p key={i} className="text-base">
            <span className="font-medium">{member.name}</span>
            {member.role && <span className="text-muted-foreground"> — {member.role}</span>}
          </p>
        ))}
      </div>
    </div>
  )
}

export function HashtagsSection({ hashtags }: { hashtags: string[] }) {
  if (!hashtags.length) return null
  return (
    <div className="flex flex-wrap gap-2">
      {hashtags.map((tag) => (
        <Badge key={tag} variant="secondary">{tag}</Badge>
      ))}
    </div>
  )
}

export function CausesSection({ causes }: { causes: string[] }) {
  if (!causes.length) return null
  return (
    <div>
      <h2 className="mb-4 text-xl font-semibold">Organization Causes</h2>
      <div className="flex flex-wrap gap-2">
        {causes.map((cause) => (
          <Badge key={cause} variant="secondary">{cause}</Badge>
        ))}
      </div>
    </div>
  )
}

export function IndustriesSection({ industries }: { industries: string[] }) {
  if (!industries.length) return null
  return (
    <div>
      <h2 className="mb-4 text-xl font-semibold">Industries & Networks</h2>
      <div className="flex flex-wrap gap-2">
        {industries.map((industry) => (
          <Badge key={industry} variant="secondary">{industry}</Badge>
        ))}
      </div>
    </div>
  )
}

export function EventNeedsSection({ event }: { event: Event }) {
  if (!event.auction_items && !event.gift_items) return null
  return (
    <div>
      <h2 className="mb-4 text-xl font-semibold tracking-tight">Event Needs</h2>
      <div className="flex flex-wrap gap-3">
        {event.auction_items && (
          <span className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-4 py-2 text-base font-medium text-amber-800 ring-1 ring-amber-200">
            <Gavel className="size-4" />
            Accepting auction items
          </span>
        )}
        {event.gift_items && (
          <span className="inline-flex items-center gap-2 rounded-full bg-sky-50 px-4 py-2 text-base font-medium text-sky-800 ring-1 ring-sky-200">
            <Gift className="size-4" />
            Seeking gift bag donations
          </span>
        )}
      </div>
    </div>
  )
}

export function VenueMap({
  event,
  locationLines,
  addressQuery,
  apiKey,
}: {
  event: Event
  locationLines: string[]
  addressQuery: string
  apiKey: string | null
}) {
  const show = Boolean(apiKey && hasMapAddress(event) && addressQuery)
  if (!show) return null
  return (
    <div>
      <h2 className="mb-4 text-xl font-semibold">Venue</h2>
      <p className="mb-3 text-base">{locationLines.join('  ')}</p>
      <div className="overflow-hidden rounded-xl">
        <iframe
          title="Event location map"
          width="100%"
          height="300"
          style={{ border: 0 }}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          src={`https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${encodeURIComponent(addressQuery)}`}
        />
      </div>
    </div>
  )
}

export function CountdownBlock({
  countdown,
  className,
}: {
  countdown: { days: number; hours: number; minutes: number }
  className?: string
}) {
  return (
    <div className={className ?? 'flex justify-center gap-6 rounded-xl bg-primary/5 p-4 text-center'}>
      <div>
        <p className="text-3xl font-semibold tabular-nums">{countdown.days}</p>
        <p className="text-sm font-medium text-muted-foreground">days</p>
      </div>
      <div>
        <p className="text-3xl font-semibold tabular-nums">{countdown.hours}</p>
        <p className="text-sm font-medium text-muted-foreground">hrs</p>
      </div>
      <div>
        <p className="text-3xl font-semibold tabular-nums">{countdown.minutes}</p>
        <p className="text-sm font-medium text-muted-foreground">min</p>
      </div>
    </div>
  )
}

export function PurchaseActions({
  event,
  organization,
  isPreview = false,
}: {
  event: Event
  organization?: DetailOrganization | null
  isPreview?: boolean
}) {
  const isExternal = event.payment_mode === 'external'
  const items = event.purchasable_items ?? []
  const ticketOnly = items.length > 0 && items.every((i) => i.kind === 'ticket')
  const freeOnly = items.length > 0 && items.every((i) => i.price_cents === 0)
  const native = !isExternal && !!organization && items.length > 0 &&
    (organization.stripe_payment_ready === true || freeOnly)
  const nativeDonate = !isExternal && !!event.donations_enabled && organization?.stripe_payment_ready === true
  const [cartOpen, setCartOpen] = useState(false)
  // Custom button text applies to native checkout only; external CTAs are always platform-aware.
  const ctaLabel = event.purchase_button_text?.trim() ||
    (ticketOnly ? (freeOnly ? 'Claim Tickets' : 'Get Tickets') : 'Support This Event')
  const platformName = externalPlatformName(event.external_platform)
  const isFundraiser = event.page_kind === 'fundraiser'
  const externalLabel = isFundraiser
    ? platformName ? `Give on ${platformName}` : 'Give'
    : platformName ? `Get Tickets on ${platformName}` : 'Get Tickets'
  const externalGlyph = !platformName

  return (
    <>
      {isExternal ? (
        event.ticket_link && (
          isPreview ? (
            <Button className="mt-3 w-full" disabled>
              {externalLabel}
              {externalGlyph && <ExternalLink className="ml-2 size-4" />}
            </Button>
          ) : (
            <Button asChild className="mt-3 w-full" onClick={() => track(event.id, 'ticket_click')}>
              <a href={event.ticket_link} target="_blank" rel="noopener noreferrer nofollow">
                {externalLabel}
                {externalGlyph && <ExternalLink className="ml-2 size-4" />}
              </a>
            </Button>
          )
        )
      ) : native ? (
        <>
          <Button
            className="mt-3 w-full"
            disabled={isPreview}
            onClick={() => {
              if (isPreview) return
              track(event.id, 'ticket_click')
              setCartOpen(true)
            }}
          >
            {ctaLabel}
          </Button>
          {!isPreview && <CartModal event={event} items={items} open={cartOpen} onOpenChange={setCartOpen} />}
        </>
      ) : null}
      {event.auction_url && (
        isPreview ? (
          <Button variant="outline" className="mt-2 w-full" disabled>
            <Gavel className="mr-2 size-4" />
            View Auction
          </Button>
        ) : (
          <Button asChild variant="outline" className="mt-2 w-full" onClick={() => track(event.id, 'auction_click')}>
            <a href={event.auction_url} target="_blank" rel="noopener noreferrer">
              <Gavel className="mr-2 size-4" />
              View Auction
            </a>
          </Button>
        )
      )}
      {nativeDonate ? (
        <DonateBlock event={event} organization={organization ?? null} isPreview={isPreview} />
      ) : (
        event.donation_url && (
          isPreview ? (
            <Button className="mt-2 w-full bg-green-600 text-white hover:bg-green-700" disabled>
              Donate
              <ExternalLink className="ml-2 size-4" />
            </Button>
          ) : (
            <Button asChild className="mt-2 w-full bg-green-600 text-white hover:bg-green-700" onClick={() => track(event.id, 'donation_click')}>
              <a href={event.donation_url} target="_blank" rel="noopener noreferrer">
                Donate
                <ExternalLink className="ml-2 size-4" />
              </a>
            </Button>
          )
        )
      )}
      {event.raffle_url && (
        isPreview ? (
          <Button className="mt-2 w-full" disabled>
            Enter Raffle
            <ExternalLink className="ml-2 size-4" />
          </Button>
        ) : (
          <Button asChild className="mt-2 w-full" onClick={() => track(event.id, 'raffle_click')}>
            <a href={event.raffle_url} target="_blank" rel="noopener noreferrer">
              Enter Raffle
              <ExternalLink className="ml-2 size-4" />
            </a>
          </Button>
        )
      )}
    </>
  )
}

export function hasPurchaseActionContent(event: Event, organization?: DetailOrganization | null): boolean {
  const isExternal = event.payment_mode === 'external'
  const items = event.purchasable_items ?? []
  const freeOnly = items.length > 0 && items.every((i) => i.price_cents === 0)
  const native = !isExternal && !!organization && items.length > 0 &&
    (organization.stripe_payment_ready === true || freeOnly)
  const nativeDonate = !isExternal && !!event.donations_enabled && organization?.stripe_payment_ready === true

  return Boolean(
    (isExternal && event.ticket_link) ||
    native ||
    event.auction_url ||
    nativeDonate ||
    event.donation_url ||
    event.raffle_url
  )
}

export function SaveEventButton({
  event,
  isSaved,
  brandFree,
  isPreview = false,
}: {
  event: Event
  isSaved: boolean
  brandFree: boolean
  isPreview?: boolean
}) {
  const { current_user } = usePage<SharedProps>().props
  if (brandFree || !current_user) return null
  return (
    <Button
      variant={isSaved ? 'default' : 'outline'}
      className="w-full"
      disabled={isPreview}
      onClick={() => {
        if (isPreview) return
        if (isSaved) {
          router.delete(`/events/${event.slug}/save`, { preserveScroll: true })
        } else {
          router.post(`/events/${event.slug}/save`, {}, { preserveScroll: true })
        }
      }}
    >
      {isSaved ? <BookmarkCheck className="size-4" /> : <Bookmark className="size-4" />}
      {isSaved ? 'Saved' : 'Save Event'}
    </Button>
  )
}

export function ShareAndCalendar({
  event,
  brandFree,
  eventUrl,
  isPreview = false,
}: {
  event: Event
  brandFree: boolean
  eventUrl: string
  isPreview?: boolean
}) {
  const isDraftPreview = event.status === 'draft'
  if (isDraftPreview || isPreview) {
    return (
      <p className="text-base text-muted-foreground">
        {isPreview
          ? 'Share and calendar links are disabled in preview.'
          : 'Share and calendar links appear after you publish this event.'}
      </p>
    )
  }
  if (brandFree) {
    return (
      <AddToCalendar
        eventId={event.id}
        eventSlug={event.slug}
        title={event.title}
        date={event.date ?? ''}
        startTime={event.start_time}
        endTime={event.end_time}
        venue={event.venue_name}
        description={event.description}
      />
    )
  }
  return (
    <div className="flex gap-2">
      <div className="flex-1">
        <ShareButtons eventId={event.id} title={event.title} url={eventUrl} />
      </div>
      <div className="flex-1">
        <AddToCalendar
          eventId={event.id}
          eventSlug={event.slug}
          title={event.title}
          date={event.date ?? ''}
          startTime={event.start_time}
          endTime={event.end_time}
          venue={event.venue_name}
          description={event.description}
        />
      </div>
    </div>
  )
}

export function OrganizationCard({
  event,
  organization,
  isFollowing,
  brandFree,
  isPreview = false,
}: {
  event: Event
  organization: DetailOrganization | null
  isFollowing: boolean
  brandFree: boolean
  isPreview?: boolean
}) {
  const { current_user } = usePage<SharedProps>().props
  if (organization) {
    return (
      <div className="border-t border-black/5 pt-6">
        <div className="flex items-center gap-3">
          <Avatar className="size-10">
            {organization.logo_url && <AvatarImage src={organization.logo_url} alt={organization.name} />}
            <AvatarFallback>{organization.name.charAt(0)}</AvatarFallback>
          </Avatar>
          <div className="flex items-center gap-1.5">
            {brandFree ? (
              <span className="font-medium">{organization.name}</span>
            ) : (
              <Link href={`/o/${organization.slug}`} className="font-medium underline-offset-4 hover:underline">
                {organization.name}
              </Link>
            )}
            {organization.verified && <VerifiedBadge className="size-3.5" />}
          </div>
        </div>
        {!brandFree && (
          <div className="mt-3">
            {current_user ? (
              <Button
                variant={isFollowing ? 'default' : 'outline'}
                size="sm"
                className="w-full"
                disabled={isPreview}
                onClick={() => {
                  if (isPreview) return
                  if (isFollowing) {
                    router.delete(`/o/${organization.slug}/follow`, { preserveScroll: true })
                  } else {
                    router.post(`/o/${organization.slug}/follow`, {}, { preserveScroll: true })
                  }
                }}
              >
                {isFollowing ? 'Following' : `Follow ${organization.name}`}
              </Button>
            ) : (
              <Button variant="outline" size="sm" className="w-full" disabled>
                Follow {organization.name}
              </Button>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="border-t border-black/5 pt-6 text-center space-y-3">
      <p className="text-base font-medium text-muted-foreground">Is this your event?</p>
      <Button className="w-full" disabled={isPreview} onClick={() => {
        if (isPreview) return
        router.post(`/events/${event.slug}/claim`)
      }}>
        Claim this listing
      </Button>
      <p className="text-sm text-muted-foreground">Link this event to your organization</p>
    </div>
  )
}

export function DetailIconRow({
  icon,
  children,
}: {
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 rounded-lg bg-primary/10 p-2">{icon}</div>
      <div>{children}</div>
    </div>
  )
}

export function donationState(event: Event, organization: DetailOrganization | null) {
  const nativeDonate = !!event.donations_enabled && organization?.stripe_payment_ready === true
  const externalDonate = nativeDonate ? null : event.donation_url
  const donationAvailable = nativeDonate || !!externalDonate
  return { nativeDonate, externalDonate, donationAvailable }
}

export function DonationPanel({
  event,
  organization,
  isPreview = false,
}: {
  event: Event
  organization: DetailOrganization | null
  isPreview?: boolean
}) {
  const { nativeDonate, externalDonate } = donationState(event, organization)

  if (nativeDonate) {
    return <DonateBlock event={event} organization={organization ?? null} isPreview={isPreview} />
  }

  if (externalDonate) {
    return (
      <div className="rounded-xl border border-primary/15 bg-primary/5 p-4">
        <div className="mb-3 flex items-center gap-2">
          <Heart className="size-4 text-primary" fill="currentColor" />
          <h3 className="text-base font-semibold">Donate</h3>
        </div>
        {isPreview ? (
          <Button className="w-full" disabled>
            Donate
            <ExternalLink className="ml-2 size-4" />
          </Button>
        ) : (
          <Button asChild className="w-full" onClick={() => track(event.id, 'donation_click')}>
            <a href={externalDonate} target="_blank" rel="noopener noreferrer">
              Donate
              <ExternalLink className="ml-2 size-4" />
            </a>
          </Button>
        )}
        {organization?.name && (
          <p className="mt-2 text-xs text-muted-foreground">
            Your tax receipt (if applicable) will come from {organization.name}.
          </p>
        )}
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-black/5 bg-card p-4">
      <p className="text-base text-muted-foreground">Donations are not available yet.</p>
    </div>
  )
}

export function MobileDonateBar({
  event,
  organization,
  isPreview = false,
}: {
  event: Event
  organization: DetailOrganization | null
  isPreview?: boolean
}) {
  const { nativeDonate, externalDonate, donationAvailable } = donationState(event, organization)
  if (!donationAvailable) return null

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 border-t border-black/10 bg-background/95 px-4 py-3 shadow-[0_-8px_24px_rgba(0,0,0,0.08)] backdrop-blur lg:hidden">
      {isPreview ? (
        <Button className="w-full" disabled>Donate</Button>
      ) : nativeDonate ? (
        <Button asChild className="w-full">
          <a href="#donate">Donate</a>
        </Button>
      ) : (
        <Button asChild className="w-full" onClick={() => track(event.id, 'donation_click')}>
          <a href={externalDonate!} target="_blank" rel="noopener noreferrer">
            Donate
            <ExternalLink className="ml-2 size-4" />
          </a>
        </Button>
      )}
    </div>
  )
}

export { Calendar, Clock, MapPin, Tag }
